// Audience rows under this key name the founder group. Its feature_flags row is
// an identity anchor (the audience foreign key needs one), not a feature: it stays
// disabled and is never read through useFeatureFlag.
export const FOUNDER_GROUP_KEY = 'founder_group'

// Pure resolver: flags control presentation, never data authorization.
export function resolveFeatureFlag({ key, flags = [], audience = [], userId, founderId, googleOAuthEnabled }) {
  const flag = flags.find(row => row.key === key)
  if (!flag || flag.enabled !== true) return false
  // Build-time provider readiness can veto OAuth, but cannot bypass the DB kill switch.
  if (key === 'google_oauth' && googleOAuthEnabled !== 'true') return false
  if (flag.tier === 'everyone') return true
  if (!userId) return false
  // Founder previews resolve from the database, not only the build-time id: a
  // member of the founder group sees every founder flag, including ones added
  // later, and a per-flag audience row still works. The build id alone left
  // every founder preview hidden without an error: the GitHub
  // VITE_FOS_FOUNDER_USER_ID variable is not set, so CI builds ship it empty (seen
  // 2026-09-28, still unset on 2026-10-02).
  if (flag.tier === 'founder') {
    return (Boolean(founderId) && userId === founderId)
      || audience.some(row => row.user_id === userId && (row.flag_key === key || row.flag_key === FOUNDER_GROUP_KEY))
  }
  if (flag.tier === 'beta') return audience.some(row => row.flag_key === key && row.user_id === userId)
  return false
}

// Memory only: a new document fetches fresh flags, including with a cached PWA bundle.
// Promise caching also coalesces React StrictMode's duplicate mount effects.
export function createFeatureFlagSession(client) {
  let flagsPromise
  const audiencePromises = new Map()
  const read = async query => {
    try {
      const { data, error } = await query
      return error ? [] : (data || [])
    } catch { return [] }
  }
  return async userId => {
    flagsPromise ||= read(client.from('feature_flags').select('key,tier,enabled'))
    if (userId && !audiencePromises.has(userId)) {
      audiencePromises.set(userId, read(client.from('feature_flag_audience')
        .select('flag_key,user_id').eq('user_id', userId)))
    }
    const [flags, audience] = await Promise.all([flagsPromise, userId ? audiencePromises.get(userId) : []])
    return { flags, audience }
  }
}
